import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpDown,
  Banknote,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Check,
  Clock3,
  CreditCard,
  FileSpreadsheet,
  FileText,
  LayoutGrid,
  LogOut,
  Printer,
  RefreshCw,
  RotateCcw,
  ScanLine,
  Search,
  SearchX,
  ShoppingCart,
  Trash2,
  TriangleAlert,
  User,
  X,
} from "lucide-react";
import Select from "react-select";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import PosModals from "../../core/modals/pos-modal/posModals";
import { fetchProductList, getProductById } from "../../api/productApi";
import { getAllCategories } from "../../api/categoryApi";
import { mockPos } from "../../api/mock/pos";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getPosOverviewReport } from "../../api/transactionReportApi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const isHttpUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value);
const looksLikeCatalog = (value) =>
  typeof value === "string" && /^\/?(catalog|catelog)\//i.test(value);

const normalizeKey = (value = "") => {
  let normalized = String(value)
    .trim()
    .replace(/^\/+/, "")
    .replace(/^catelog\//i, "catalog/");
  if (!/^catalog\//i.test(normalized)) {
    normalized = `catalog/${normalized.split("/").pop()}`;
  }
  return normalized;
};

const toPublicUrl = (value) => {
  if (!value) return "";
  if (isHttpUrl(value)) return value;
  if (looksLikeCatalog(value)) {
    const base = (
      process.env.REACT_APP_API_IMAGE_BASE_URL ||
      "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
    ).replace(/\/+$/, "");
    return `${base}/${normalizeKey(value)}`;
  }
  return value;
};

const normalizeId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const DEFAULT_REPORT_LIMIT = 100;
const DEFAULT_REPORT_TYPE = "cash";
const MAX_PDF_ORDERS = 25;
const PRODUCTS_PER_PAGE = 12;
const tr = (key) =>
  typeof window !== "undefined" && window.translate
    ? window.translate(key)
    : key;
const POS_AVAILABILITY_EVENT = "pos:product-availability";
const emitPosAvailability = (payload) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(POS_AVAILABILITY_EVENT, {
      detail: payload,
    }),
  );
};
const syncAvailabilityWithCart = (items = []) => {
  const ids = new Set();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const selectedId = String(item?._id || "").trim();
    const baseId = String(item?.baseProductId || "").trim();
    if (selectedId) ids.add(selectedId);
    if (baseId) ids.add(baseId);
  });
  emitPosAvailability({
    kind: "cart-sync",
    productIds: Array.from(ids),
  });
};
const getCurrentLang = () =>
  typeof window !== "undefined" && localStorage.getItem("lang") === "ar"
    ? "ar"
    : "en";

const toYmd = (dateLike) => {
  const date = new Date(dateLike);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const toInt = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

const normalizeName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ar || "";
  return String(value);
};

const getItemTaxRate = (item = {}) => {
  const candidates = [
    item?.taxValue,
    item?.tax,
    item?.taxRate,
    item?.tax_type,
    item?.taxType,
  ];

  for (const raw of candidates) {
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }

  return 0;
};

const getItemDiscountValue = (item = {}) => {
  const candidates = [
    item?.discountValue,
    item?.discount,
    item?.discountRate,
    item?.discount_rate,
    item?.pricing?.discountValue,
  ];

  for (const raw of candidates) {
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  }

  return 0;
};

const getItemDiscountAmount = (item = {}) => {
  const unitPrice = Number(item?.Price ?? item?.price ?? 0);
  const quantity = Number(item?.quantity ?? 0);
  const lineTotal = unitPrice * quantity;
  const discountValue = getItemDiscountValue(item);

  if (!Number.isFinite(lineTotal) || lineTotal <= 0 || discountValue <= 0) {
    return 0;
  }

  const discountType = String(item?.discountType || "")
    .trim()
    .toLowerCase();

  if (["cash", "flat", "fixed", "amount", "value"].some((k) => discountType.includes(k))) {
    return discountValue * quantity;
  }

  if (["percentage", "percent", "%"].some((k) => discountType.includes(k))) {
    return (discountValue / 100) * lineTotal;
  }

  // Fallback for missing/unknown discount type: keep previous POS behavior.
  if (discountValue <= 100) return (discountValue / 100) * lineTotal;
  return discountValue * quantity;
};

const getDefaultReportRange = () => {
  const to = toYmd(new Date());
  const firstDayOfMonth = new Date();
  firstDayOfMonth.setDate(1);
  return { from: toYmd(firstDayOfMonth), to };
};

const buildReportParams = ({ userId, from, to, limit, type } = {}) => {
  const fallbackRange = getDefaultReportRange();
  const parsedLimit = Number(limit);
  const isNoLimit =
    limit === "all" || limit === "" || limit === null || limit === undefined;
  const normalizedType = type === "all" ? undefined : type;
  return {
    from: from || fallbackRange.from,
    to: to || fallbackRange.to,
    userId: userId || undefined,
    limit: isNoLimit
      ? undefined
      : Number.isFinite(parsedLimit) && parsedLimit > 0
        ? parsedLimit
        : DEFAULT_REPORT_LIMIT,
    type: normalizedType || DEFAULT_REPORT_TYPE,
  };
};

const downloadOverviewExcel = (report, params) => {
  const metricKey = tr("Metric");
  const valueKey = tr("Value");
  const summary = report?.summary || {};
  const summaryRows = [
    {
      [metricKey]: tr("Total Transactions"),
      [valueKey]: toInt(summary.totalTransactions),
    },
    {
      [metricKey]: tr("Total Sales Amount"),
      [valueKey]: Number(toAmount(summary.totalSalesAmount)),
    },
    {
      [metricKey]: tr("Total Received Amount"),
      [valueKey]: Number(toAmount(summary.totalReceivedAmount)),
    },
    {
      [metricKey]: tr("Total Items Sold"),
      [valueKey]: toInt(summary.totalItemsSold),
    },
    { [metricKey]: tr("From"), [valueKey]: params.from || "-" },
    { [metricKey]: tr("To"), [valueKey]: params.to || "-" },
    { [metricKey]: tr("Payment Type"), [valueKey]: params.type || tr("All") },
    { [metricKey]: tr("Limits"), [valueKey]: params.limit ?? tr("All") },
  ];
  const orderRows = (report?.orders || []).map((order, index) => {
    const sellerName = [
      normalizeName(order?.seller?.firstName),
      normalizeName(order?.seller?.lastName),
    ]
      .join(" ")
      .trim();
    return {
      Sr: index + 1,
      OrderId: order?.orderId || "-",
      TransactionId: order?.transactionId || "-",
      Type: order?.type || "-",
      TotalAmount: Number(toAmount(order?.totalAmount)),
      ReceivedAmount: Number(toAmount(order?.receivedAmount)),
      ItemsSold: toInt(order?.itemsSold),
      Seller: sellerName || order?.seller?.username || "-",
      CreatedAt: order?.createdAt
        ? new Date(order.createdAt).toLocaleString()
        : "-",
    };
  });
  const userRows = (report?.userSales || []).map((user, index) => ({
    Sr: index + 1,
    UserId: user?.userId || "-",
    Name:
      [normalizeName(user?.firstName), normalizeName(user?.lastName)]
        .join(" ")
        .trim() || "-",
    Username: user?.username || "-",
    Role: user?.role || "-",
    TotalTransactions: toInt(user?.totalTransactions),
    TotalSalesAmount: Number(toAmount(user?.totalSalesAmount)),
    TotalItemsSold: toInt(user?.totalItemsSold),
  }));
  const productRows = (report?.productSales || []).map((product, index) => ({
    Sr: index + 1,
    ProductId: product?.productId || "-",
    Name: product?.name || "-",
    TotalQuantitySold: toInt(product?.totalQuantitySold),
    TotalSalesAmount: Number(toAmount(product?.totalSalesAmount)),
    TransactionCount: toInt(product?.transactionCount),
    SellerCount: toInt(product?.sellerCount),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(summaryRows),
    tr("Summary"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(orderRows),
    tr("Orders"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(userRows),
    tr("UserSales"),
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(productRows),
    tr("ProductSales"),
  );
  XLSX.writeFile(wb, `pos_overview_${params.from}_to_${params.to}.xlsx`);
};

const downloadOverviewPdf = (report, params) => {
  const colors = {
    coral: [254, 159, 67],
    coralLight: [255, 243, 230],
    coralMid: [254, 219, 179],
    textDark: [26, 26, 46],
    textMid: [85, 85, 85],
    textLight: [130, 130, 145],
    border: [240, 228, 227],
  };
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const generatedAt = new Date().toLocaleString();
  const summary = report?.summary || {};
  const summaryCards = [
    {
      label: tr("Total Transactions"),
      value: String(toInt(summary.totalTransactions)),
    },
    {
      label: tr("Total Sales"),
      value: `$${toAmount(summary.totalSalesAmount)}`,
    },
    {
      label: tr("Total Received"),
      value: `$${toAmount(summary.totalReceivedAmount)}`,
    },
    { label: tr("Items Sold"), value: String(toInt(summary.totalItemsSold)) },
  ];
  doc.setFillColor(...colors.coral);
  doc.rect(0, 0, pageWidth, 86, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(tr("POS Selling Overview Report"), marginX, 38);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    `${tr("Range")}: ${params.from || "-"} - ${params.to || "-"}   |   ${tr("Type")}: ${params.type || tr("All")}   |   ${tr("Limits")}: ${params.limit ?? tr("All")}`,
    marginX,
    58,
  );
  doc.text(`${tr("Generated")}: ${generatedAt}`, marginX, 74);
  let cursorY = 112;
  const cardGap = 12;
  const cardWidth = (pageWidth - marginX * 2 - cardGap) / 2;
  const cardHeight = 70;
  summaryCards.forEach((card, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = marginX + col * (cardWidth + cardGap);
    const y = cursorY + row * (cardHeight + cardGap);
    doc.setFillColor(...colors.coralLight);
    doc.setDrawColor(...colors.coralMid);
    doc.roundedRect(x, y, cardWidth, cardHeight, 8, 8, "FD");
    doc.setTextColor(...colors.textMid);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(card.label, x + 12, y + 24);
    doc.setTextColor(...colors.textDark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(card.value, x + 12, y + 50);
  });
  cursorY += cardHeight * 2 + cardGap + 24;
  doc.setTextColor(...colors.textDark);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(tr("Recent Orders"), marginX, cursorY);
  cursorY += 10;
  const orders = report?.orders || [];
  const tableRows = orders.length
    ? orders.slice(0, MAX_PDF_ORDERS).map((order, idx) => {
        const sellerName = [
          normalizeName(order?.seller?.firstName),
          normalizeName(order?.seller?.lastName),
        ]
          .join(" ")
          .trim();
        return [
          idx + 1,
          order?.orderId || order?.transactionId || "-",
          order?.type || "-",
          sellerName || order?.seller?.username || "-",
          toInt(order?.itemsSold),
          `$${toAmount(order?.totalAmount)}`,
          `$${toAmount(order?.receivedAmount)}`,
          order?.createdAt ? new Date(order.createdAt).toLocaleString() : "-",
        ];
      })
    : [
        [
          "-",
          tr("No orders found for this selected range."),
          "-",
          "-",
          "-",
          "-",
          "-",
          "-",
        ],
      ];
  autoTable(doc, {
    startY: cursorY + 8,
    margin: { left: marginX, right: marginX },
    head: [
      [
        "#",
        tr("Order / Txn"),
        tr("Type"),
        tr("Seller"),
        tr("Items"),
        tr("Total"),
        tr("Received"),
        tr("Created"),
      ],
    ],
    body: tableRows,
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 6,
      textColor: colors.textDark,
      lineColor: colors.border,
      lineWidth: 0.5,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: colors.coral,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      lineColor: colors.coral,
    },
    alternateRowStyles: { fillColor: [253, 248, 247] },
    columnStyles: {
      0: { halign: "center", cellWidth: 24 },
      1: { cellWidth: 82 },
      2: { cellWidth: 46 },
      3: { cellWidth: 88 },
      4: { halign: "center", cellWidth: 34 },
      5: { halign: "right", cellWidth: 52 },
      6: { halign: "right", cellWidth: 60 },
      7: { cellWidth: 110 },
    },
    didDrawPage: () => {
      doc.setDrawColor(...colors.border);
      doc.line(marginX, pageHeight - 32, pageWidth - marginX, pageHeight - 32);
      doc.setFontSize(9);
      doc.setTextColor(...colors.textLight);
      doc.text(tr("Hamilton POS - Overview Report"), marginX, pageHeight - 16);
    },
  });
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setFontSize(9);
    doc.setTextColor(...colors.textLight);
    doc.text(
      `${tr("Page")} ${page} ${tr("of")} ${totalPages}`,
      pageWidth - marginX,
      pageHeight - 16,
      { align: "right" },
    );
  }
  doc.save(`pos_overview_${params.from}_to_${params.to}.pdf`);
};

/* ─── GLOBAL STYLES ─────────────────────────────────────────────────── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');

  :root {
    --coral: #f39f1a;
    --coral-light: #fff4df;
    --coral-mid: #f6d39b;
    --sidebar-bg: #fff;
    --bg: #fffaf2;
    --card-bg: #ffffff;
    --text-dark: #1a1a2e;
    --text-mid: #555;
    --text-light: #999;
    --border: #f3e2c4;
    --shadow-sm: 0 2px 10px rgba(243,159,26,0.08);
    --shadow-md: 0 6px 24px rgba(243,159,26,0.14);
    --radius-sm: 10px;
    --radius-md: 16px;
    --radius-lg: 22px;
  }

  .pos-page body, .pos-figma-wrap * {
    font-family: 'Nunito', sans-serif !important;
  }

  .pos-figma-wrap {
    display: flex;
    height: 100vh;
    overflow: hidden;
    background: var(--bg);
    font-family: 'Nunito', sans-serif;
  }

  /* ── Left vertical nav ── */
  .pos-sidenav {
    width: 72px;
    background: var(--sidebar-bg);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
    gap: 8px;
    flex-shrink: 0;
  }

  .pos-sidenav-logo {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: var(--coral-light);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  .pos-sidenav-logo svg { width: 22px; height: 22px; fill: var(--coral); }

  .pos-icon { width: 18px; height: 18px; stroke-width: 2.2; flex-shrink: 0; }
  .pos-icon-sm { width: 16px; height: 16px; }
  .pos-icon-md { width: 20px; height: 20px; }
  .pos-icon-lg { width: 22px; height: 22px; }

  .pos-sidenav-item {
    width: 44px; height: 44px; border-radius: var(--radius-sm);
    display: flex; align-items: center; justify-content: center;
    color: var(--text-light); cursor: pointer; transition: all 0.2s;
    font-size: 18px; text-decoration: none;
  }
  .pos-sidenav-item:hover, .pos-sidenav-item.active { background: var(--coral-light); color: var(--coral); }
  .pos-sidenav-bottom { margin-top: auto; }
  .pos-report-menu-wrap { position: relative; }

  .pos-report-menu {
    position: absolute; left: calc(100% + 10px); top: 0; min-width: 320px;
    background: #fff; border: 1px solid var(--border); border-radius: 12px;
    box-shadow: 0 10px 24px rgba(15,23,42,0.12); padding: 8px; z-index: 20;
  }

  .pos-report-filter-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
    margin-bottom: 8px; padding: 4px 2px 10px; border-bottom: 1px solid var(--border);
  }

  .pos-report-field { display: flex; flex-direction: column; gap: 4px; }
  .pos-report-field span { font-size: 11px; font-weight: 700; color: var(--text-light); }
  .pos-report-field input, .pos-report-field select {
    width: 100%; height: 34px; border: 1px solid var(--border); border-radius: 8px;
    padding: 0 10px; font-size: 12px; color: var(--text-dark); background: #fff;
  }
  .pos-report-field input:focus, .pos-report-field select:focus {
    border-color: var(--coral); box-shadow: 0 0 0 2px rgba(243,159,26,0.18); outline: none;
  }

  .pos-report-menu-btn {
    width: 100%; border: 0; background: transparent; display: flex; align-items: center;
    gap: 8px; padding: 8px 10px; border-radius: 8px; color: var(--text-dark);
    font-size: 13px; font-weight: 700; text-align: left; cursor: pointer;
  }
  .pos-report-menu-btn:hover { background: var(--coral-light); color: var(--coral); }
  .pos-report-menu-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  .pos-main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }

  .pos-topbar {
    background: var(--card-bg); border-bottom: 1px solid var(--border);
    padding: 14px 28px; display: flex; align-items: center; justify-content: flex-end;
    gap: 16px; flex-wrap: wrap; flex-shrink: 0;
  }

  .pos-category-search {
    width: 100%; max-width: 380px; position: relative; margin-bottom: 14px;
  }
  .pos-category-search input {
    width: 100%; border: 1.5px solid #e6eaed; border-radius: 12px;
    padding: 11px 14px 11px 42px; font-size: 14px; font-family: 'Nunito', sans-serif;
    background: #fff; color: var(--text-dark); outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .pos-category-search input:focus {
    border-color: var(--coral); box-shadow: 0 0 0 3px rgba(243,159,26,0.18);
  }
  .pos-category-search .search-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    display: inline-flex; align-items: center; justify-content: center; color: var(--text-light);
  }

  .pos-cashier-badge {
    display: flex; align-items: center; gap: 10px; margin-left: auto;
    padding: 8px 16px 8px 8px; border-radius: 50px; background: var(--bg); border: 1px solid var(--border);
  }
  .pos-cashier-avatar {
    width: 34px; height: 34px; border-radius: 50%; background: var(--coral-light);
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .pos-cashier-avatar svg { width: 18px; height: 18px; }
  .pos-cashier-info { line-height: 1.2; }
  .pos-cashier-info strong { display: block; font-size: 13px; font-weight: 700; color: var(--text-dark); }
  .pos-cashier-info span { font-size: 11px; color: var(--text-light); }

  .pos-topbar-btn {
    padding: 9px 16px; border-radius: 50px; font-size: 13px; font-weight: 600;
    font-family: 'Nunito', sans-serif; border: none; cursor: pointer;
    display: flex; align-items: center; gap: 6px; transition: all 0.2s; text-decoration: none;
  }
  .pos-topbar-btn.teal { background: #e0f5f4; color: #0f9d94; }
  .pos-topbar-btn.teal:hover { background: #c5eeec; }
  .pos-topbar-btn.indigo { background: #eef0fc; color: #4f5de0; }
  .pos-topbar-btn.indigo:hover { background: #dfe2f9; }
  .pos-topbar-btn.info { background: #e5f4fc; color: #1a8fc4; }
  .pos-topbar-btn.info:hover { background: #cce8f5; }

  .pos-body { flex: 1; display: flex; overflow: hidden; }

  /* ── Product panel ── */
  .pos-product-panel { flex: 1; overflow-y: auto; padding: 24px 28px; }
  .pos-product-panel::-webkit-scrollbar { width: 4px; }
  .pos-product-panel::-webkit-scrollbar-track { background: transparent; }
  .pos-product-panel::-webkit-scrollbar-thumb { background: var(--coral-mid); border-radius: 4px; }

  .pos-section-title { font-size: 18px; font-weight: 800; color: var(--text-dark); margin-bottom: 16px; }
  .pos-category-title { font-size: 52px; line-height: 1.05; margin-bottom: 18px; }

  /* ── Category cards ── */
  .pos-cat-slider-wrap {
    position: relative; margin-bottom: 8px; padding: 2px 40px 4px; overflow: hidden;
  }
  .pos-cat-slider-wrap.force-ltr,
  .pos-cat-slider-wrap.force-ltr .pos-cat-slider,
  .pos-cat-slider-wrap.force-ltr .pos-cat-slider .swiper-wrapper { direction: ltr !important; }
  .pos-cat-slider-wrap.force-ltr .pos-cat-prev { left: 2px !important; right: auto !important; }
  .pos-cat-slider-wrap.force-ltr .pos-cat-next { right: 2px !important; left: auto !important; }
  .pos-cat-slider { overflow: hidden; width: 100%; padding: 2px 2px 10px; }
  .pos-cat-slider .swiper-slide { height: auto; }

  .pos-cat-nav {
    position: absolute; top: 46%; transform: translateY(-50%);
    width: 30px; height: 30px; border-radius: 999px; border: 1px solid var(--border);
    background: #fff; color: var(--text-mid); display: inline-flex; align-items: center;
    justify-content: center; box-shadow: 0 3px 10px rgba(15,23,42,0.12);
    cursor: pointer; z-index: 3; transition: all 0.2s ease;
  }
  .pos-cat-prev { left: 2px; }
  .pos-cat-next { right: 2px; }
  .pos-cat-nav:hover { border-color: var(--coral); color: var(--coral); background: var(--coral-light); }
  .pos-cat-nav.swiper-button-disabled { opacity: 0.35; cursor: not-allowed; }

  .pos-cat-card {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    background: var(--card-bg); border: 1.5px solid var(--border); border-radius: 14px;
    padding: 12px 10px; cursor: pointer; transition: all 0.2s; width: 100%;
    min-height: 108px; text-align: center; user-select: none; overflow: hidden;
  }
  .pos-cat-card:hover { border-color: var(--coral); box-shadow: var(--shadow-sm); transform: translateY(-2px); }
  .pos-cat-card.active { border-color: var(--coral); background: var(--coral-light); box-shadow: 0 8px 20px rgba(243,159,26,0.2); }

  .pos-cat-img-wrap {
    width: 30px; height: 30px; border-radius: 8px; overflow: hidden; margin-bottom: 8px;
    display: flex; align-items: center; justify-content: center; background: var(--bg); flex-shrink: 0;
  }
  .pos-cat-img-wrap img { width: 100%; height: 100%; object-fit: cover; }
  .pos-cat-emoji { font-size: 26px; margin-bottom: 8px; line-height: 1; flex-shrink: 0; }
  .pos-cat-name {
    font-size: 13px; font-weight: 700; color: var(--text-dark); line-height: 1.2;
    max-width: 100%; overflow: hidden; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  }
  .pos-cat-card.active .pos-cat-name { color: var(--coral); }

  body.layout-mode-rtl .pos-cat-prev, [dir="rtl"] .pos-cat-prev { left: auto; right: 2px; }
  body.layout-mode-rtl .pos-cat-next, [dir="rtl"] .pos-cat-next { right: auto; left: 2px; }

  /* ── Products section header ── */
  .pos-products-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 16px; margin-top: 28px;
  }
  .pos-sort-btn {
    display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-mid);
    background: none; border: none; cursor: pointer; font-family: 'Nunito', sans-serif; font-weight: 600;
  }

  /* ── Product grid ── */
  .pos-product-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
  .pos-prod-card {
    background: var(--card-bg); border-radius: var(--radius-md); overflow: hidden;
    border: 1.5px solid var(--border); cursor: pointer; transition: all 0.22s; position: relative;
  }
  .pos-prod-card:hover { box-shadow: var(--shadow-md); transform: translateY(-3px); border-color: var(--coral-mid); }
  .pos-prod-card.selected { border-color: var(--coral); box-shadow: 0 0 0 3px rgba(243,159,26,0.2); }
  .pos-prod-card.selected::after {
    content: '✓'; position: absolute; top: 8px; right: 8px; width: 24px; height: 24px;
    background: var(--coral); color: #fff; border-radius: 50%; font-size: 13px; font-weight: 700;
    display: flex; align-items: center; justify-content: center; line-height: 24px; text-align: center;
  }
  .pos-prod-img-wrap { width: 100%; height: 140px; overflow: hidden; background: var(--coral-light); }
  .pos-prod-img-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
  .pos-prod-card:hover .pos-prod-img-wrap img { transform: scale(1.05); }
  .pos-prod-body { padding: 12px 14px; }
  .pos-prod-cat { font-size: 11px; color: var(--coral); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
  .pos-prod-name { font-size: 14px; font-weight: 700; color: var(--text-dark); line-height: 1.3; margin-bottom: 8px; }
  .pos-prod-footer { display: flex; align-items: center; justify-content: space-between; }
  .pos-prod-qty { font-size: 12px; color: var(--text-light); font-weight: 600; }
  .pos-prod-price { font-size: 15px; font-weight: 800; color: var(--coral); }
  .pos-prod-variants { display: flex; flex-wrap: wrap; gap: 4px; padding: 0 14px 10px; }
  .pos-prod-variant-btn {
    font-size: 11px; font-weight: 600; font-family: 'Nunito', sans-serif; padding: 3px 10px;
    border-radius: 50px; border: 1.5px solid var(--coral-mid); background: var(--coral-light);
    color: var(--coral); cursor: pointer; transition: all 0.15s;
  }
  .pos-prod-variant-btn:hover { background: var(--coral); color: #fff; border-color: var(--coral); }

  /* ── Pagination ── */
  .pos-products-pagination {
    margin-top: 18px; display: flex; align-items: center; justify-content: center;
    gap: 8px; flex-wrap: wrap;
  }
  .pos-page-btn {
    min-width: 34px; height: 34px; border-radius: 10px; border: 1px solid var(--border);
    background: #fff; color: var(--text-dark); font-size: 13px; font-weight: 700;
    padding: 0 10px; display: inline-flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s ease;
  }
  .pos-page-btn:hover { border-color: var(--coral); color: var(--coral); background: var(--coral-light); }
  .pos-page-btn.active { border-color: var(--coral); background: var(--coral); color: #fff; }
  .pos-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .pos-page-meta { margin-left: 6px; margin-right: 6px; color: var(--text-mid); font-size: 13px; font-weight: 700; }

  /* ── Billing panel (RIGHT) ── */
  .pos-billing-panel {
    width: 380px;
    flex-shrink: 0;
    background: var(--card-bg);
    border-left: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .pos-billing-panel .product-order-list {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .pos-billing-panel .product-order-list::-webkit-scrollbar { width: 4px; }
  .pos-billing-panel .product-order-list::-webkit-scrollbar-track { background: transparent; }
  .pos-billing-panel .product-order-list::-webkit-scrollbar-thumb { background: var(--coral-mid); border-radius: 4px; }

  /* ── Status popup ── */
  .status-popup-overlay {
    position: fixed; inset: 0; background: rgba(15,23,42,0.45); backdrop-filter: blur(3px);
    z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px;
  }
  .status-popup-card {
    width: 100%; max-width: 440px; background: #fff; border-radius: 20px;
    box-shadow: 0 20px 60px rgba(15,23,42,0.18); overflow: hidden; animation: popupFadeIn 0.25s ease;
  }
  .status-popup-top { padding: 24px 24px 16px; text-align: center; }
  .status-popup-icon {
    width: 72px; height: 72px; border-radius: 999px; display: inline-flex;
    align-items: center; justify-content: center; font-size: 34px; margin-bottom: 14px;
  }
  .status-popup-icon.success { background: rgba(34,197,94,0.12); color: #16a34a; }
  .status-popup-icon.error { background: rgba(239,68,68,0.12); color: #dc2626; }
  .status-popup-title { margin: 0 0 8px; font-size: 24px; font-weight: 700; color: #0f172a; }
  .status-popup-message { margin: 0; font-size: 15px; line-height: 1.6; color: #475569; }
  .status-popup-footer { padding: 18px 24px 24px; display: flex; justify-content: center; }
  .status-popup-btn {
    min-width: 140px; height: 46px; border: none; border-radius: 12px; font-size: 15px;
    font-weight: 600; color: #fff; background: linear-gradient(135deg, var(--coral), #e38806);
    box-shadow: 0 10px 24px rgba(243,159,26,0.24); transition: transform 0.2s ease, box-shadow 0.2s ease; cursor: pointer;
  }
  .status-popup-btn:hover { transform: translateY(-1px); box-shadow: 0 14px 28px rgba(243,159,26,0.28); }

  .checkout-spinner {
    width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.35);
    border-top-color: #ffffff; border-radius: 50%; display: inline-block;
    animation: checkoutSpin 0.7s linear infinite;
  }
  @keyframes checkoutSpin { to { transform: rotate(360deg); } }
  @keyframes popupFadeIn {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── Billing panel overrides ── */
  .pos-billing-panel .btn-block .btn-secondary {
    background: var(--coral) !important; border-color: var(--coral) !important;
    border-radius: 14px !important; font-size: 15px !important; font-weight: 700 !important;
    font-family: 'Nunito', sans-serif !important; padding: 14px !important; transition: all 0.2s !important;
  }
  .pos-billing-panel .btn-block .btn-secondary:hover { background: #d4564e !important; border-color: #d4564e !important; }

  .pos-billing-panel .payment-item {
    border-radius: 12px !important; border: 1.5px solid var(--border) !important; transition: all 0.2s !important;
  }
  .pos-billing-panel .payment-item.active,
  .pos-billing-panel .payment-item:hover {
    border-color: var(--coral) !important; background: var(--coral-light) !important; color: var(--coral) !important;
  }

  /* react-select coral theme */
  .pos-billing-panel .react-select__control {
    border-color: var(--border) !important; border-radius: 10px !important;
    font-family: 'Nunito', sans-serif !important; font-size: 13px !important;
  }
  .pos-billing-panel .react-select__control:hover { border-color: var(--coral) !important; }
  .pos-billing-panel .react-select__control--is-focused {
    border-color: var(--coral) !important; box-shadow: 0 0 0 2px rgba(243,159,26,0.2) !important;
  }
  .pos-billing-panel .react-select__option--is-selected { background: var(--coral) !important; }
  .pos-billing-panel .react-select__option--is-focused { background: var(--coral-light) !important; color: var(--coral) !important; }

  .pos-billing-panel .btn-white {
    border: 1.5px solid var(--border) !important; border-radius: 12px !important;
    font-weight: 700 !important; font-family: 'Nunito', sans-serif !important;
    color: var(--text-dark) !important; transition: all 0.2s !important;
  }
  .pos-billing-panel .btn-white:hover {
    border-color: var(--coral) !important; color: var(--coral) !important; background: var(--coral-light) !important;
  }
  .pos-billing-panel .btn-primary { background: var(--coral) !important; border-color: var(--coral) !important; border-radius: 10px !important; }
  .pos-billing-panel .btn-primary:hover { background: #d4564e !important; border-color: #d4564e !important; }
  .pos-billing-panel .text-teal { color: var(--coral) !important; }

  /* ── no products ── */
  .pos-no-products { grid-column: 1 / -1; text-align: center; padding: 60px 20px; color: var(--text-light); }
  .pos-no-products .icon { margin-bottom: 12px; opacity: 0.4; }
  .pos-no-products .icon svg { width: 48px; height: 48px; }

  /* ══════════════════════════════════════════════
     BILLING PANEL CARDS
  ══════════════════════════════════════════════ */

  .pos-billing-panel .pos-panel-card {
    background: linear-gradient(180deg, #ffffff 0%, #fffdf8 100%);
    border: 1px solid var(--border);
    border-radius: 14px;
    box-shadow: 0 4px 12px rgba(243,159,26,0.07);
    padding: 14px;
    margin: 10px 12px 0;
    flex-shrink: 0;
  }
  .pos-billing-panel .pos-panel-card h4 { margin: 0 0 12px; font-size: 15px; font-weight: 800; color: var(--text-dark); }
  .pos-billing-panel .pos-customer-card .input-block { margin-bottom: 10px; }
  .pos-billing-panel .pos-customer-card .input-block:last-child { margin-bottom: 0; }

  /* ── Cart card wrapper ── */
  .pos-billing-panel .pos-cart-card {
    margin: 10px 12px 0;
    border: 1px solid var(--border);
    border-radius: 14px;
    box-shadow: 0 4px 12px rgba(243,159,26,0.07);
    background: #fff;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    overflow: visible;
  }

  /* ── Cart card header ── */
  .pos-billing-panel .pos-cart-card .head-text {
    background: linear-gradient(180deg, #fff9ef 0%, #ffffff 100%);
    border-bottom: 1px solid var(--border);
    border-radius: 14px 14px 0 0;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-shrink: 0;
  }
  .pos-billing-panel .pos-cart-card .head-text .cart-head-left {
    display: flex; flex-direction: column; gap: 3px; min-width: 0;
  }
  .pos-billing-panel .pos-cart-card .head-text h5 {
    margin: 0; font-size: 15px; font-weight: 800;
    display: flex; align-items: center; gap: 8px; color: var(--text-dark);
  }
  .pos-billing-panel .pos-cart-card .head-text h5 .count {
    min-width: 22px; height: 22px; border-radius: 999px; display: inline-flex;
    align-items: center; justify-content: center; background: var(--coral);
    color: #fff; font-size: 11px; font-weight: 800; padding: 0 6px;
  }
  .pos-billing-panel .pos-cart-card .head-text .cart-head-total {
    font-size: 11px; color: var(--text-mid); font-weight: 700;
  }
  .pos-billing-panel .pos-cart-card .head-text .link-danger {
    flex-shrink: 0;
    border: 1px solid #ffd8b6; background: #fff4e8; color: #d97706 !important;
    border-radius: 999px; padding: 5px 10px; font-size: 11px; font-weight: 800;
    display: inline-flex; align-items: center; gap: 5px; line-height: 1;
    text-decoration: none; white-space: nowrap; transition: all 0.15s;
  }
  .pos-billing-panel .pos-cart-card .head-text .link-danger:hover { background: #ffe8d0; border-color: #f8c28f; }

  /* ═══════════════════════════════════════════════════
     CART ITEMS — fully responsive flexbox layout
  ═══════════════════════════════════════════════════ */

  /* Scrollable list */
  .pos-billing-panel .pos-cart-card .pos-cart-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 340px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
  }
  .pos-billing-panel .pos-cart-card .pos-cart-items::-webkit-scrollbar { width: 3px; }
  .pos-billing-panel .pos-cart-card .pos-cart-items::-webkit-scrollbar-track { background: transparent; }
  .pos-billing-panel .pos-cart-card .pos-cart-items::-webkit-scrollbar-thumb { background: var(--coral-mid); border-radius: 4px; }

  /* ── Empty cart ── */
  .pos-billing-panel .pos-cart-card .empty-cart {
    border: 1px dashed #f1d5ab; background: #fff9ef; border-radius: 12px;
    padding: 30px 16px; text-align: center; color: var(--text-light);
  }

  /* ── Single cart item row ── */
  .pos-billing-panel .pos-cart-item {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
    background: #fff;
    border: 1px solid #f2debf;
    border-radius: 12px;
    padding: 10px;
    box-shadow: 0 2px 8px rgba(243,159,26,0.08);
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  /* ── Product image ── */
  .pos-billing-panel .pos-cart-item__img {
    width: 60px;
    height: 60px;
    min-width: 60px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #f2dfc5;
    display: block;
    flex-shrink: 0;
    text-decoration: none;
    background: #fff6ea;
  }
  .pos-billing-panel .pos-cart-item__img img {
    width: 100%; height: 100%; object-fit: cover; display: block;
  }

  /* ── Info block ── */
  .pos-billing-panel .pos-cart-item__info {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;
  }

  /* Meta row: SKU + tags */
  .pos-billing-panel .pos-cart-item__meta {
    display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
  }
  .pos-billing-panel .pos-cart-item__sku {
    display: inline-flex; align-items: center; border-radius: 999px;
    padding: 2px 6px; background: #111827; color: #fff; font-size: 9px;
    font-weight: 800; letter-spacing: 0.2px; direction: ltr; white-space: nowrap; flex-shrink: 0;
  }
  .pos-billing-panel .pos-cart-item__tag {
    display: inline-flex; align-items: center; border-radius: 999px;
    padding: 2px 6px; font-size: 9px; font-weight: 700; line-height: 1.3;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 85px;
  }
  .pos-billing-panel .pos-cart-item__tag--category { background: #eef8ff; color: #0b5e8e; border: 1px solid #d8ebf7; }
  .pos-billing-panel .pos-cart-item__tag--variant { background: #fff4e5; color: #9a4f08; border: 1px solid #f5d7b7; }

  /* Product name */
  .pos-billing-panel .pos-cart-item__name {
    margin: 0; font-size: 12px; font-weight: 800; line-height: 1.3; min-width: 0;
  }
  .pos-billing-panel .pos-cart-item__name a {
    color: #1a1a2e; text-decoration: none; display: -webkit-box;
    -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word;
  }
  .pos-billing-panel .pos-cart-item__name a:hover { color: var(--coral); }

  /* Pricing chips */
  .pos-billing-panel .pos-cart-item__pricing {
    display: flex; align-items: center; gap: 4px; flex-wrap: wrap; min-width: 0;
  }
  .pos-billing-panel .pos-cart-item__chip {
    background: #f8fafc; border: 1px solid #e8edf3; border-radius: 999px;
    padding: 2px 6px; font-size: 9px; font-weight: 700; color: #334155;
    line-height: 1.3; direction: ltr; white-space: nowrap; flex-shrink: 0;
  }
  .pos-billing-panel .pos-cart-item__total {
    border-radius: 999px; padding: 2px 7px; background: #fff7ed;
    border: 1px solid #fed7aa; color: #b45309; font-size: 9px; font-weight: 800;
    direction: ltr; white-space: nowrap; flex-shrink: 0;
  }

  /* ── Right actions: delete + qty ── */
  .pos-billing-panel .pos-cart-item__actions {
    display: flex; flex-direction: column; align-items: center;
    justify-content: flex-start; gap: 6px; flex-shrink: 0;
  }
  .pos-billing-panel .pos-cart-item__remove {
    width: 26px; height: 26px; border-radius: 7px; border: 1px solid #fecaca;
    background: #fff5f5; color: #dc2626; display: inline-flex; align-items: center;
    justify-content: center; text-decoration: none; flex-shrink: 0; cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .pos-billing-panel .pos-cart-item__remove:hover { background: #fee2e2; border-color: #fca5a5; }

  /* Qty stepper */
  .pos-billing-panel .pos-cart-item__qty {
    display: inline-flex; align-items: center; border: 1px solid #f4d7ae;
    border-radius: 999px; overflow: hidden; background: #fffdf7; height: 28px; flex-shrink: 0;
  }
  .pos-billing-panel .pos-cart-item__qty button {
    width: 24px; height: 26px; border: 0; background: transparent; color: #92400e;
    font-size: 15px; font-weight: 800; line-height: 1; display: inline-flex;
    align-items: center; justify-content: center; cursor: pointer; padding: 0;
    transition: background 0.15s; font-family: 'Nunito', sans-serif;
  }
  .pos-billing-panel .pos-cart-item__qty button:hover { background: #fff1da; }
  .pos-billing-panel .pos-cart-item__qty-count {
    min-width: 24px; text-align: center; font-size: 11px; font-weight: 800;
    color: #111827; padding: 0 2px; font-family: 'Nunito', sans-serif;
  }

  /* ── RTL support ── */
  [dir="rtl"] .pos-billing-panel .pos-cart-item { direction: rtl; }
  [dir="rtl"] .pos-billing-panel .pos-cart-item__actions { padding-left: 0; padding-right: 2px; }
  body.layout-mode-rtl .pos-billing-panel .pos-cart-item__total,
  [dir="rtl"] .pos-billing-panel .pos-cart-item__total { margin-left: 0; margin-right: auto; }
  body.layout-mode-rtl .pos-billing-panel .pos-cart-card .head-text .link-danger,
  [dir="rtl"] .pos-billing-panel .pos-cart-card .head-text .link-danger { flex-direction: row-reverse; }

  /* ── Summary card ── */
  .pos-billing-panel .pos-summary-card .selling-info {
    border: 1px solid #f3e5cf; border-radius: 12px; padding: 10px;
    background: #fff; margin-bottom: 12px;
  }
  .pos-billing-panel .pos-summary-card .order-total {
    border: 1px solid #f3e5cf; border-radius: 12px; padding: 8px 10px; background: #ffffff;
  }
  .pos-billing-panel .pos-summary-card .order-total table { margin-bottom: 0; }
  .pos-billing-panel .pos-summary-card .order-total tr:last-child td { border-top: 1px solid #efe3d1; padding-top: 10px; }

  /* ── Responsive ── */
  @media (max-width: 1300px) {
    .pos-billing-panel { width: 340px; }
    .pos-billing-panel .pos-cart-item__tag { max-width: 72px; }
  }

  @media (max-width: 992px) {
    .pos-sidenav { display: none; }
    .pos-billing-panel { width: 300px; }
    .pos-product-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); }
    .pos-category-title { font-size: 40px; }
    .pos-cat-card { min-height: 96px; }
    .pos-billing-panel .pos-cart-item { padding: 8px; gap: 8px; }
    .pos-billing-panel .pos-cart-item__img { width: 52px; height: 52px; min-width: 52px; }
    .pos-billing-panel .pos-cart-item__tag { display: none; }
  }

  @media (max-width: 768px) {
    .pos-body { flex-direction: column; }
    .pos-billing-panel { width: 100%; height: 50vh; border-left: none; border-top: 1px solid var(--border); }
    .pos-category-title { font-size: 32px; }
    .pos-topbar { justify-content: flex-start; }
  }

  @media (max-width: 575px) {
    .pos-cat-slider-wrap { padding-left: 0; padding-right: 0; }
    .pos-cat-nav { display: none; }
    .pos-billing-panel .pos-panel-card { margin: 10px 8px 0; padding: 10px; }
    .pos-billing-panel .pos-cart-card { margin: 10px 8px 0; }
    .pos-billing-panel .pos-cart-card .pos-cart-items { max-height: 240px; padding: 8px; gap: 6px; }
    .pos-billing-panel .pos-cart-item {
      flex-wrap: wrap; gap: 7px; padding: 8px;
    }
    .pos-billing-panel .pos-cart-item__img { width: 48px; height: 48px; min-width: 48px; }
    .pos-billing-panel .pos-cart-item__info { flex: 1 1 calc(100% - 48px - 17px); }
    .pos-billing-panel .pos-cart-item__actions {
      flex-direction: row; width: 100%; justify-content: space-between;
      align-items: center; padding: 4px 0 0; border-top: 1px solid #f5e8d8;
    }
    [dir="rtl"] .pos-billing-panel .pos-cart-item__actions { flex-direction: row-reverse; }
    .pos-billing-panel .pos-cart-card .head-text { flex-wrap: wrap; align-items: flex-start; }
    .pos-billing-panel .pos-cart-card .head-text .cart-head-total { font-size: 10px; }
  }
`;

/* ─── CATEGORY EMOJI MAP ─────────────────────────────────────────────── */
const categoryEmojiMap = { all: "📦", popular: "⭐" };
const getCategoryEmoji = (name) => {
  const key = (name || "").toLowerCase().trim();
  return (
    categoryEmojiMap[key] ||
    Object.entries(categoryEmojiMap).find(([k]) => key.includes(k))?.[1] ||
    "📦"
  );
};

/* ─── COMPONENT ─────────────────────────────────────────────────────── */
const Pos2 = () => {
  const location = useLocation();
  const currentLang = getCurrentLang();
  const isArabicUi = currentLang === "ar";

  const [activeTab, setActiveTab] = useState("all");
  const [productPage, setProductPage] = useState(1);
  const [demo, setDemo] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [receivedAmount, setReceivedAmount] = useState("");
  const [payingAmount, setPayingAmount] = useState(0);
  const [change, setChange] = useState("0.00");
  const [paymentType, setPaymentType] = useState({
    value: "cash",
    label: tr("Cash"),
  });
  const [paymentReceiver, setPaymentReceiver] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [saleNote, setSaleNote] = useState("");
  const [staffNote, setStaffNote] = useState("");

  // const [orderTaxRate, setOrderTaxRate] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);

  const [stockModal, setStockModal] = useState({
    show: false,
    productName: "",
    maxQty: 0,
  });
  const [lastTransaction, setLastTransaction] = useState(null);
  const invoiceLang = currentLang;
  const [statusPopup, setStatusPopup] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [reportLoading, setReportLoading] = useState("");
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [reportFrom, setReportFrom] = useState(
    () => getDefaultReportRange().from,
  );
  const [reportTo, setReportTo] = useState(() => getDefaultReportRange().to);
  const [reportLimit, setReportLimit] = useState(String(DEFAULT_REPORT_LIMIT));
  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPE);
  const reportMenuRef = useRef(null);
  const categorySwiperRef = useRef(null);

  const handleCategoryNext = () => {
    if (categorySwiperRef.current) categorySwiperRef.current.slideNext();
  };
  const handleCategoryPrev = () => {
    if (categorySwiperRef.current) categorySwiperRef.current.slidePrev();
  };

  const getLocalizedCategoryName = (nameValue) => {
    if (nameValue && typeof nameValue === "object") {
      if (isArabicUi) return nameValue.ar || nameValue.en || tr("Unnamed");
      return nameValue.en || nameValue.ar || tr("Unnamed");
    }
    const text = String(nameValue || "").trim();
    return text ? tr(text) : tr("Unnamed");
  };

  const nf = (n, lang) =>
    (typeof n === "number" ? n : Number(n || 0)).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
    );

  const buildInvoiceHtml = (tx, lang = "en") => {
    const L = {
      invoice: tr("Invoice"),
      orderId: tr("Order ID"),
      date: tr("Date"),
      paymentType: tr("Payment Type"),
      cashier: tr("Payment Receiver"),
      items: tr("Items"),
      qty: tr("Qty"),
      price: tr("Price"),
      lineTotal: tr("Total"),
      subtotal: tr("Sub Total"),
      tax: tr("Tax"),
      discount: tr("Discount"),
      total: tr("Total"),
      received: tr("Received"),
      paying: tr("Paying"),
      change: tr("Change"),
      thankYou: tr("Thank you for your purchase!"),
    };
    const dir = lang === "ar" ? "rtl" : "ltr";
    const alignStart = lang === "ar" ? "right" : "left";
    const alignEnd = lang === "ar" ? "left" : "right";
    const subtotal2 = (tx.items || []).reduce(
      (s, i) => s + Number(i.price || 0) * Number(i.quantity || 0),
      0,
    );
    const total =
      typeof tx.totalAmount === "number" ? tx.totalAmount : subtotal2;
    const taxAmount = Number(
      tx.taxAmount ?? tx.productTaxAmount ?? tx.tax ?? 0,
    );
    const discountAmount = Math.abs(
      Number(tx.discountAmount ?? tx.discount ?? 0),
    );
    const received = tx.receivedAmount ?? 0;
    const paying = tx.payingAmount ?? total;
    const changeValue = tx.change ?? Math.max(0, received - paying);
    const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
      lang === "ar" ? "ar-SA" : "en-US",
    );
    const rows = (tx.items || [])
      .map(
        (item) =>
          `<tr><td>${item.name || ""}</td><td style="text-align:center">${nf(item.quantity || 0, lang)}</td><td style="text-align:${alignEnd}">${nf(item.price || 0, lang)}</td><td style="text-align:${alignEnd}">${nf((item.price || 0) * (item.quantity || 0), lang)}</td></tr>`,
      )
      .join("");
    return `<!DOCTYPE html><html lang="${lang}" dir="${dir}"><head><meta charset="utf-8"/><title>${L.invoice}</title><style>body{font-family:Arial,sans-serif;margin:0;padding:16px}.wrap{max-width:360px;margin:0 auto}h1{font-size:18px;margin:0 0 8px;text-align:center}.meta{font-size:12px;margin-bottom:12px}.meta div{display:flex;justify-content:space-between}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:6px 4px;border-bottom:1px dashed #ddd}th{text-align:${alignStart};font-weight:bold;background:#f8f8f8}tfoot td{border:0;padding:4px 4px}.total-row td{border-top:1px solid #000;font-weight:bold}.thanks{text-align:center;margin-top:12px;font-size:12px}.end{text-align:${alignEnd}}.start{text-align:${alignStart}}</style></head><body><div class="wrap"><h1>${L.invoice}</h1><div class="meta"><div><span>${L.orderId}</span><strong>${tx.orderId || "-"}</strong></div><div><span>${L.date}</span><strong>${todayStr}</strong></div><div><span>${L.paymentType}</span><strong>${tx.type || "-"}</strong></div><div><span>${L.cashier}</span><strong>${tx.paymentReceiver || "-"}</strong></div></div><table><thead><tr><th class="start">${L.items}</th><th style="text-align:center">${L.qty}</th><th class="end">${L.price}</th><th class="end">${L.lineTotal}</th></tr></thead><tbody>${rows || `<tr><td colspan="4" style="text-align:center">-</td></tr>`}</tbody><tfoot><tr><td colspan="3" class="end">${L.subtotal}</td><td class="end">${nf(subtotal2, lang)}</td></tr><tr><td colspan="3" class="end">${L.tax}</td><td class="end">${nf(taxAmount, lang)}</td></tr><tr><td colspan="3" class="end">${L.discount}</td><td class="end">-${nf(discountAmount, lang)}</td></tr><tr class="total-row"><td colspan="3" class="end">${L.total}</td><td class="end">${nf(total, lang)}</td></tr><tr><td colspan="3" class="end">${L.received}</td><td class="end">${nf(received, lang)}</td></tr><tr><td colspan="3" class="end">${L.paying}</td><td class="end">${nf(paying, lang)}</td></tr><tr><td colspan="3" class="end">${L.change}</td><td class="end">${nf(changeValue, lang)}</td></tr></tfoot></table><div class="thanks">${L.thankYou}</div></div><script>window.onload=function(){window.print();setTimeout(()=>window.close(),300);};</script></body></html>`;
  };

  const printInvoice = (lang = invoiceLang, tx = lastTransaction) => {
    const activeLang = getCurrentLang();
    const targetLang = activeLang || (lang === "ar" ? "ar" : "en");
    if (!tx) {
      alert(
        tr("No completed transaction to print. Please place an order first."),
      );
      return;
    }
    const popup = window.open("", "_blank", "width=420,height=600");
    if (!popup) {
      alert(tr("Popup blocked. Please allow popups to print the invoice."));
      return;
    }
    popup.document.open();
    popup.document.write(buildInvoiceHtml(tx, targetLang));
    popup.document.close();
  };

  useEffect(() => {
    document.body.classList.add("pos-page");
    return () => {
      document.body.classList.remove("pos-page");
    };
  }, [location.pathname]);

  /* ── Load categories ── */
  useEffect(() => {
    const loadCategories = async () => {
      const fallbackCategories = mockPos.categories.map((category) => {
        const rawImage = category.image || "category-default.svg";
        const isLocalAsset =
          !/^data:image/i.test(rawImage) &&
          !isHttpUrl(rawImage) &&
          !looksLikeCatalog(rawImage);
        const imageUrl = /^data:image/i.test(rawImage)
          ? rawImage
          : isLocalAsset
            ? `assets/img/categories/${rawImage}`
            : toPublicUrl(rawImage);
        return {
          _id: normalizeId(category._id || category.id || category.CategoryId),
          name: category.name || category.CategoryName || tr("Unnamed"),
          imageUrl,
          isLocalAsset,
        };
      });
      try {
        const categoryResponse = await getAllCategories();
        const categoryData = Array.isArray(categoryResponse)
          ? categoryResponse
          : Array.isArray(categoryResponse?.data)
            ? categoryResponse.data
            : [];
        const normalized = categoryData.map((category) => {
          const rawImage = category.image || "category-default.svg";
          const isLocalAsset =
            !/^data:image/i.test(rawImage) &&
            !isHttpUrl(rawImage) &&
            !looksLikeCatalog(rawImage);
          const imageUrl = /^data:image/i.test(rawImage)
            ? rawImage
            : isLocalAsset
              ? `assets/img/categories/${rawImage}`
              : toPublicUrl(rawImage);
          return {
            _id: normalizeId(
              category._id || category.id || category.CategoryId,
            ),
            name: category.name || category.CategoryName || tr("Unnamed"),
            imageUrl,
            isLocalAsset,
          };
        });
        setCategories([
          { _id: "all", name: tr("All"), imageUrl: "", isLocalAsset: false },
          ...(normalized.length ? normalized : fallbackCategories),
        ]);
        setDemo(false);
      } catch {
        setDemo(true);
        setCategories([
          { _id: "all", name: tr("All"), imageUrl: "", isLocalAsset: false },
          ...fallbackCategories,
        ]);
      }
    };
    loadCategories();
  }, []);

  /* ── Load products ── */
  useEffect(() => {
    const loadProducts = async () => {
      const fallbackCategoryMap = {};
      mockPos.categories.forEach((category) => {
        fallbackCategoryMap[
          String(category.name || "")
            .toLowerCase()
            .trim()
        ] = normalizeId(category.id);
      });
      const fallbackProducts = mockPos.products.map((product) => ({
        ...product,
        _id: String(product._id || product.id),
        Product_Name: product.Product_Name || product.name || tr("Unnamed"),
        baseProductName: product.Product_Name || product.name || tr("Unnamed"),
        variantName: "",
        categoryName:
          String(product.Category || "").trim() || tr("Uncategorized"),
        CategoryId:
          fallbackCategoryMap[
            String(product.Category || "")
              .toLowerCase()
              .trim()
          ] || "uncategorized",
        Price: Number(product.Price ?? product.price ?? 0),
        Qty: Number(product.Qty ?? product.quantity ?? 0),
        quantity: 1,
      }));
      try {
        const productsResponse = await fetchProductList();
        const result = Array.isArray(productsResponse)
          ? productsResponse
          : Array.isArray(productsResponse?.data)
            ? productsResponse.data
            : [];
        const categoryResponse = await getAllCategories();
        const categoryData = Array.isArray(categoryResponse)
          ? categoryResponse
          : Array.isArray(categoryResponse?.data)
            ? categoryResponse.data
            : [];
        const categoryMap = {};
        categoryData.forEach((category) => {
          const name = (category.name || category.CategoryName || "")
            .toLowerCase()
            .trim();
          const id = normalizeId(
            category._id || category.id || category.CategoryId,
          );
          categoryMap[name] = id;
        });
        const normalizedProducts = result.flatMap((product) => {
          const rawCategoryName =
            typeof product.Category === "object"
              ? product.Category?.name || product.Category?.CategoryName
              : product.Category || "";
          const categoryName = String(rawCategoryName).toLowerCase().trim();
          const displayCategoryName =
            String(rawCategoryName || "").trim() || tr("Uncategorized");
          const baseName =
            product.Product_Name || product.name || tr("Unnamed");
          const baseProduct = {
            ...product,
            _id: String(product._id || product.id || product.ProductId),
            Product_Name: baseName,
            baseProductName: baseName,
            variantName: "",
            categoryName: displayCategoryName,
            CategoryId: categoryMap[categoryName] || "uncategorized",
            quantity: 1,
            Price: Number(product.Price ?? product.price ?? 0),
            Qty: Number(product.Qty ?? product.quantity ?? 0),
            tax: product.tax ?? product.taxValue ?? 0,
            taxValue: product.taxValue ?? product.tax ?? 0,
            taxType: product.taxType || "",
            discountValue: product.discountValue ?? product.discount ?? 0,
            discountType: product.discountType || product.discount_type || "",
          };
          if (
            Array.isArray(product.variantItems) &&
            product.variantItems.length > 0
          ) {
            return [
              {
                ...baseProduct,
                allVariants: product.variantItems.map((variant) => {
                  const variantName = String(variant.name || "").trim();
                  return {
                    ...baseProduct,
                    baseProductId: baseProduct._id,
                    _id: String(variant._id),
                    variantId: variant._id,
                    name: variantName || tr("Variant"),
                    Product_Name: variantName
                      ? `${baseName} - ${variantName}`
                      : baseName,
                    baseProductName: baseName,
                    variantName,
                    Price: Number(variant.price ?? 0),
                    Qty: Number(variant.quantity ?? 0),
                    tax: variant.taxValue ?? variant.tax ?? 0,
                    taxValue: variant.taxValue ?? variant.tax ?? 0,
                    discountValue: variant.discountValue ?? variant.discount ?? 0,
                    discountType: variant.discountType || variant.discount_type || "",
                    taxType: variant.taxType,
                    image: variant.image || baseProduct.image,
                    isVariant: true,
                  };
                }),
              },
            ];
          }
          return [baseProduct];
        });
        setProducts(
          normalizedProducts.length ? normalizedProducts : fallbackProducts,
        );
        setDemo(false);
      } catch {
        setDemo(true);
        setProducts(fallbackProducts);
      }
    };
    loadProducts();
  }, []);

  const categoryNameMap = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      const id = normalizeId(category._id);
      if (id && id !== "all") map.set(id, category.name || tr("Uncategorized"));
    });
    return map;
  }, [categories]);

  const filteredProducts = useMemo(() => {
    const byCategory =
      activeTab === "all"
        ? products
        : products.filter(
            (p) => normalizeId(p.CategoryId) === normalizeId(activeTab),
          );
    const term = searchText.trim().toLowerCase();
    if (!term) return byCategory;
    return byCategory.filter((p) => {
      const name = String(p.Product_Name || p.name || "").toLowerCase();
      const category = String(
        p.categoryName ||
          (typeof p.Category === "object"
            ? p.Category?.name || p.Category?.CategoryName
            : p.Category) ||
          "",
      ).toLowerCase();
      return name.includes(term) || category.includes(term);
    });
  }, [activeTab, products, searchText]);

  const totalProductPages = useMemo(
    () => Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)),
    [filteredProducts.length],
  );
  const paginatedProducts = useMemo(() => {
    const start = (productPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, productPage]);
  const visiblePageButtons = useMemo(() => {
    const maxButtons = 5;
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, productPage - half);
    let end = Math.min(totalProductPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) start = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [productPage, totalProductPages]);

  const productSearchOptions = useMemo(
    () =>
      filteredProducts
        .slice(0, 50)
        .map((p) => ({
          value: String(p._id),
          label: p.Product_Name || p.name || tr("Unnamed"),
        })),
    [filteredProducts],
  );

  useEffect(() => {
    setProductPage(1);
  }, [activeTab, searchText]);
  useEffect(() => {
    if (productPage > totalProductPages) setProductPage(totalProductPages);
  }, [productPage, totalProductPages]);

  const publishProductAvailability = async (product) => {
    const selectedProductId = String(product?._id || product?.id || "");
    const fallbackProductId = String(product?.baseProductId || "");
    const availabilityProductId = selectedProductId || fallbackProductId;
    if (!availabilityProductId) return;

    const fallbackName =
      product?.baseProductName ||
      product?.Product_Name ||
      product?.name ||
      tr("Unnamed");

    emitPosAvailability({
      loading: true,
      error: false,
      productId: availabilityProductId,
      productName: fallbackName,
    });

    try {
      let response;
      try {
        response = await getProductById(availabilityProductId);
      } catch (primaryError) {
        if (
          fallbackProductId &&
          fallbackProductId !== availabilityProductId
        ) {
          response = await getProductById(fallbackProductId);
        } else {
          throw primaryError;
        }
      }
      const detail = response?.data || response || {};

      emitPosAvailability({
        loading: false,
        error: false,
        productId: availabilityProductId,
        productName:
          detail?.name || detail?.Product_Name || detail?.productName || fallbackName,
        data: {
          store: detail?.store || detail?.storeName || "",
          storeId: detail?.storeId || "",
          warehouse: detail?.warehouse || detail?.warehouseName || "",
          warehouseId: detail?.warehouseId || "",
          availability: Array.isArray(detail?.availability)
            ? detail.availability
            : [],
        },
      });
    } catch (error) {
      emitPosAvailability({
        loading: false,
        error: true,
        message: tr("Availability unavailable"),
        productId: availabilityProductId,
        productName: fallbackName,
      });
      console.error("Failed to fetch product availability:", error);
    }
  };

  const handleProductClick = async (product) => {
    const productId = String(product._id || product.id);
    void publishProductAvailability(product);
    if (selectedItems.some((item) => String(item._id) === productId)) return;
    try {
      let detailedProduct = product;
      if (!product.isVariant && !demo) {
        const response = await getProductById(productId);
        detailedProduct = response?.data || response;
      }
      const stockQty = product.isVariant
        ? Number(product.Qty ?? 0)
        : Number(detailedProduct.quantity ?? product.Qty ?? 0);
      const baseProductName =
        product.baseProductName ||
        detailedProduct.Product_Name ||
        detailedProduct.name ||
        product.Product_Name ||
        product.name ||
        tr("Unnamed");
      const variantName = String(
        product.variantName || (product.isVariant ? product.name : "") || "",
      ).trim();
      const categoryName =
        product.categoryName ||
        (typeof product.Category === "object"
          ? product.Category?.name || product.Category?.CategoryName
          : product.Category) ||
        categoryNameMap.get(normalizeId(product.CategoryId)) ||
        tr("Uncategorized");
      const item = {
        ...product,
        _id: productId,
        Product_Name: variantName
          ? `${baseProductName} - ${variantName}`
          : baseProductName,
        baseProductName,
        variantName,
        categoryName: String(categoryName).trim() || tr("Uncategorized"),
        Price: Number(product.Price ?? detailedProduct.price ?? 0),
        quantity: 1,
        stockQty,
        isVariant: Boolean(product.isVariant),
        taxType: detailedProduct.taxType || product.taxType || "",
        tax:
          detailedProduct.taxValue ??
          detailedProduct.tax ??
          product.tax ??
          product.taxValue ??
          product.taxRate ??
          product.tax_type ??
          0,
        taxValue:
          detailedProduct.taxValue ??
          detailedProduct.tax ??
          product.tax ??
          product.taxValue ??
          product.taxRate ??
          product.tax_type ??
          0,
        discountValue:
          detailedProduct.discountValue ??
          detailedProduct.discount ??
          detailedProduct.pricing?.discountValue ??
          product.discountValue ??
          product.discount ??
          product.discountRate ??
          product.discount_rate ??
          0,
        discountType:
          detailedProduct.discountType ||
          detailedProduct.pricing?.discountType ||
          product.discountType ||
          product.discount_type ||
          "",
      };
      if (!demo && stockQty <= 0) {
        setStockModal({
          show: true,
          productName: item.Product_Name,
          maxQty: 0,
        });
        return;
      }
      setSelectedItems((prev) => [...prev, item]);
    } catch {
      console.log("Error fetching product details");
    }
  };

  const handleQuantityChange = (id, newQty) => {
    const idStr = String(id);
    setSelectedItems((prev) => {
      const exists = prev.find((item) => String(item._id) === idStr);
      if (!exists) return prev;
      if (newQty <= 0) return prev.filter((item) => String(item._id) !== idStr);
      const max = Number(exists.stockQty ?? Infinity);
      if (newQty > max) {
        setStockModal({
          show: true,
          productName: exists.Product_Name,
          maxQty: max,
        });
        return prev;
      }
      return prev.map((item) =>
        String(item._id) === idStr ? { ...item, quantity: newQty } : item,
      );
    });
  };

  const handleRemoveItem = (id) => {
    const idStr = String(id);
    setSelectedItems((prev) =>
      prev.filter((item) => String(item._id) !== idStr),
    );
  };
  const handleClearAll = () => setSelectedItems([]);

  useEffect(() => {
    syncAvailabilityWithCart(selectedItems);
  }, [selectedItems]);

  const getItemDisplayName = (item) =>
    item.baseProductName || item.Product_Name || item.name || tr("Unnamed");
  const getItemVariantName = (item) =>
    String(item.variantName || (item.isVariant ? item.name : "") || "").trim();
  const getItemCategoryName = (item) =>
    String(
      item.categoryName ||
        (typeof item.Category === "object"
          ? item.Category?.name || item.Category?.CategoryName
          : item.Category) ||
        categoryNameMap.get(normalizeId(item.CategoryId)) ||
        tr("Uncategorized"),
    ).trim();

  const subtotal = selectedItems.reduce(
    (sum, item) => sum + Number(item.Price || 0) * Number(item.quantity || 0),
    0,
  );
  const itemTax = selectedItems.reduce((sum, item) => {
    const t = getItemTaxRate(item);
    return (
      sum + (t / 100) * Number(item.Price || 0) * Number(item.quantity || 0)
    );
  }, 0);
  const itemDiscount = selectedItems.reduce((sum, item) => {
    return sum + getItemDiscountAmount(item);
  }, 0);
  const discount = (discountRate / 100) * subtotal + itemDiscount;
  const totalPayable = subtotal + itemTax + shippingFee - discount;
  // const orderTax = (orderTaxRate / 100) * subtotal;
  // const totalPayable = subtotal + itemTax + orderTax + shippingFee - discount;

  useEffect(() => {
    const total = Number.isFinite(totalPayable) ? totalPayable : 0;
    setPayingAmount(Number(total.toFixed(2)));
  }, [totalPayable]);

  const parseAmount = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const handleQuickCashAdd = (amount) => {
    setReceivedAmount((parseAmount(receivedAmount) + amount).toFixed(2));
  };

  useEffect(() => {
    const received = parseAmount(receivedAmount);
    const payable = parseAmount(payingAmount);
    const diff = received - payable > 0 ? received - payable : 0;
    setChange(diff.toFixed(2));
  }, [receivedAmount, payingAmount]);

  const openStatusPopup = (type, title, message) =>
    setStatusPopup({ show: true, type, title, message });
  const closeStatusPopup = () =>
    setStatusPopup({ show: false, type: "success", title: "", message: "" });

  useEffect(() => {
    if (!showReportMenu) return undefined;
    const handleOutsideClick = (event) => {
      if (!reportMenuRef.current?.contains(event.target))
        setShowReportMenu(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setShowReportMenu(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showReportMenu]);

  const handleReportDownload = async (format) => {
    if (reportLoading) return;
    setReportLoading(format);
    try {
      const params = buildReportParams({
        userId: localStorage.getItem("userId"),
        from: reportFrom,
        to: reportTo,
        limit: reportLimit,
        type: reportType,
      });
      const report = await getPosOverviewReport(params);
      if (!report?.success)
        throw new Error(report?.message || tr("Failed to fetch report data."));
      if (format === "excel") {
        downloadOverviewExcel(report, params);
      } else {
        downloadOverviewPdf(report, params);
      }
      setShowReportMenu(false);
    } catch (error) {
      openStatusPopup(
        "error",
        tr("Report Failed"),
        error?.response?.data?.message ||
          error?.message ||
          tr("Failed to download report. Please try again."),
      );
    } finally {
      setReportLoading("");
    }
  };

  const handleOrderApi = async () => {
    const token = localStorage.getItem("token");
    const storeId = localStorage.getItem("storeId");
    if (!selectedItems.length) {
      alert(tr("No items selected."));
      return false;
    }
    const validItems = selectedItems.filter(
      (item) => item && item._id && Number(item.quantity) > 0,
    );
    if (!validItems.length) {
      alert(tr("No valid items to process."));
      return false;
    }
    const finalPayingAmount = parseFloat(
      Number(payingAmount || totalPayable).toFixed(2),
    );
    const finalReceivedAmount =
      paymentType?.value === "cash"
        ? parseFloat(Number(receivedAmount || 0).toFixed(2))
        : finalPayingAmount;
    const finalChange =
      paymentType?.value === "cash"
        ? parseFloat(
            Math.max(0, finalReceivedAmount - finalPayingAmount).toFixed(2),
          )
        : 0;
    const orderPayload = {
      orderId: `ORD-${Date.now()}`,
      subtotalAmount: parseFloat(Number(subtotal).toFixed(2)),
      taxAmount: parseFloat(Number(itemTax).toFixed(2)),
      discountAmount: parseFloat(Number(discount).toFixed(2)),
      shippingAmount: parseFloat(Number(shippingFee).toFixed(2)),
      totalAmount: parseFloat(totalPayable.toFixed(2)),
      items: validItems.map((item) => ({
        productId: item.baseProductId || item._id,
        variantId: item.isVariant ? item._id : undefined,
        name: item.Product_Name || item.name || tr("Unnamed"),
        quantity: Number(item.quantity || 0),
        price: Number(item.Price || 0),
      })),
      paymentReceiver,
      paymentNote,
      saleNote,
      staffNote,
      paymentType: paymentType?.value || "cash",
      receivedAmount: finalReceivedAmount,
      payingAmount: finalPayingAmount,
      change: finalChange,
    };
    const response = await axios.post(
      `${API_BASE_URL}api/orders/`,
      orderPayload,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "X-Store-Id": storeId || undefined,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      },
    );
    return response?.data?.success;
  };

  const handlePlaceOrder = async () => {
    try {
      if (!selectedItems.length) {
        alert(tr("No items selected."));
        return false;
      }
      const token = localStorage.getItem("token");
      const storeId = localStorage.getItem("storeId");
      const validItems = selectedItems.filter(
        (item) => item && item._id && Number(item.quantity) > 0,
      );
      if (!validItems.length) {
        alert(tr("No valid items to process."));
        return false;
      }
      const finalPayingAmount = parseFloat(
        Number(payingAmount || totalPayable).toFixed(2),
      );
      const finalReceivedAmount =
        paymentType?.value === "cash"
          ? parseFloat(Number(receivedAmount || 0).toFixed(2))
          : finalPayingAmount;
      const finalChange =
        paymentType?.value === "cash"
          ? parseFloat(
              Math.max(0, finalReceivedAmount - finalPayingAmount).toFixed(2),
            )
          : 0;
      if (
        paymentType?.value === "cash" &&
        finalReceivedAmount < finalPayingAmount
      ) {
        alert(
          tr(
            "Received amount cannot be less than paying amount for cash payment.",
          ),
        );
        return false;
      }
      const payload = {
        orderId: `ORD-${Date.now()}`,
        type: paymentType?.value || "cash",
        receivedAmount: finalReceivedAmount,
        payingAmount: finalPayingAmount,
        change: finalChange,
        paymentReceiver,
        paymentNote,
        saleNote,
        staffNote,
        subtotalAmount: parseFloat(Number(subtotal).toFixed(2)),
        taxAmount: parseFloat(Number(itemTax).toFixed(2)),
        discountAmount: parseFloat(Number(discount).toFixed(2)),
        shippingAmount: parseFloat(Number(shippingFee).toFixed(2)),
        totalAmount: parseFloat(totalPayable.toFixed(2)),
        items: validItems.map((item) => ({
          productId: item.baseProductId || item._id,
          variantId: item.isVariant ? item._id : undefined,
          name: item.Product_Name || item.name || tr("Unnamed"),
          quantity: Number(item.quantity || 0),
          price: Number(item.Price || 0),
        })),
      };
      const response = await axios.post(
        `${API_BASE_URL}api/transactions/`,
        payload,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "X-Store-Id": storeId || undefined,
          },
          withCredentials: true,
        },
      );
      if (response?.data?.success) {
        const responseTx = response?.data?.transaction || {};
        const preferNonZeroAmount = (responseValue, fallbackValue) => {
          const fallbackNum = Number(fallbackValue ?? 0);
          const responseNum = Number(responseValue);

          if (!Number.isFinite(fallbackNum) && !Number.isFinite(responseNum)) {
            return 0;
          }
          if (!Number.isFinite(fallbackNum)) return responseNum;
          if (!Number.isFinite(responseNum)) return fallbackNum;
          if (responseNum === 0 && fallbackNum !== 0) return fallbackNum;
          return responseNum;
        };
        const tx = {
          ...payload,
          ...responseTx,
          subtotalAmount: preferNonZeroAmount(
            responseTx.subtotalAmount,
            payload.subtotalAmount,
          ),
          taxAmount: preferNonZeroAmount(responseTx.taxAmount, payload.taxAmount),
          discountAmount: preferNonZeroAmount(
            responseTx.discountAmount,
            payload.discountAmount,
          ),
          shippingAmount: preferNonZeroAmount(
            responseTx.shippingAmount,
            payload.shippingAmount,
          ),
          createdAt: responseTx.createdAt || Date.now(),
        };
        setLastTransaction(tx);
        handleClearAll();
        setReceivedAmount("");
        setPayingAmount(0);
        setChange("0.00");
        setPaymentReceiver("");
        setPaymentNote("");
        setSaleNote("");
        setStaffNote("");
        setPaymentType({ value: "cash", label: tr("Cash") });
        // setOrderTaxRate(0);
        setShippingFee(0);
        setDiscountRate(0);
        return true;
      }
      return false;
    } catch {
      openStatusPopup(
        "error",
        tr("Transaction Failed"),
        tr("Failed to submit transaction."),
      );
      return false;
    }
  };

  const handleGrandTotalCheckout = async () => {
    setIsCheckingOut(true);
    try {
      if (
        paymentType?.value === "cash" &&
        parseAmount(receivedAmount) < parseAmount(payingAmount || totalPayable)
      ) {
        openStatusPopup(
          "error",
          tr("Payment Error"),
          tr(
            "Received amount cannot be less than paying amount for cash payment.",
          ),
        );
        return;
      }
      const orderCreated = await handleOrderApi();
      if (!orderCreated) {
        openStatusPopup(
          "error",
          tr("Order Failed"),
          tr("Failed to create order."),
        );
        return;
      }
      const transactionCreated = await handlePlaceOrder();
      if (!transactionCreated) {
        openStatusPopup(
          "error",
          tr("Transaction Failed"),
          tr("Order created but transaction failed."),
        );
        return;
      }
      openStatusPopup(
        "success",
        tr("Success"),
        tr("Order and transaction created successfully!"),
      );
    } catch {
      openStatusPopup(
        "error",
        tr("Checkout Failed"),
        tr("Failed to complete checkout."),
      );
    } finally {
      setIsCheckingOut(false);
    }
  };

  const customerOptions = [
    { value: "walkInCustomer", label: tr("Walk-in Customer") },
  ];
  const quickCashAmounts = [5, 10, 20, 50, 100, 500];
  // const taxOptions = [
  //   { value: 0, label: tr("No Tax") },
  //   { value: 5, label: `${tr("VAT")} 5% (${tr("GCC Standard")})` },
  //   { value: 15, label: `${tr("VAT")} 15% (${tr("Saudi Arabia")})` },
  //   {
  //     value: 50,
  //     label: `${tr("Excise Tax")} 50% (${tr("Carbonated Drinks")})`,
  //   },
  //   {
  //     value: 100,
  //     label: `${tr("Excise Tax")} 100% (${tr("Tobacco / Energy Drinks")})`,
  //   },
  // ];
  // const selectedTaxOption =
  //   taxOptions.find((o) => Number(o.value) === Number(orderTaxRate)) ||
  //   taxOptions[0];
  const shippingOptions = [0, 5, 10, 15, 20, 25, 30].map((v) => ({
    value: v,
    label: String(v),
  }));
  const discountOptions = [0, 5, 10, 15, 20, 25, 30].map((v) => ({
    value: v,
    label: `${v}%`,
  }));

  /* ─── RENDER ─────────────────────────────────────────────────────── */
  return (
    <>
      <style>{globalStyles}</style>

      <div className="pos-figma-wrap" dir={isArabicUi ? "rtl" : undefined}>
        {/* ── Left vertical nav ── */}
        <nav className="pos-sidenav">
          <div className="pos-sidenav-logo">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="5" fill="white" />
            </svg>
          </div>
          <Link
            to="#"
            className="pos-sidenav-item active"
            data-bs-toggle="modal"
            data-bs-target="#orders"
            title={tr("Orders")}
          >
            <LayoutGrid className="pos-icon-md" />
          </Link>
          <Link
            to="#"
            className="pos-sidenav-item"
            data-bs-toggle="modal"
            data-bs-target="#recents"
            title={tr("Transactions")}
          >
            <Clock3 className="pos-icon-md" />
          </Link>
          <div className="pos-report-menu-wrap" ref={reportMenuRef}>
            <Link
              to="#"
              className="pos-sidenav-item"
              title={tr("Reports")}
              onClick={(event) => {
                event.preventDefault();
                setShowReportMenu((prev) => !prev);
              }}
            >
              <BarChart3 className="pos-icon-md" />
            </Link>
            {showReportMenu && (
              <div className="pos-report-menu">
                <div className="pos-report-filter-grid">
                  <label className="pos-report-field">
                    <span>{tr("From")}</span>
                    <input
                      type="date"
                      value={reportFrom}
                      max={reportTo || undefined}
                      onChange={(event) => setReportFrom(event.target.value)}
                    />
                  </label>
                  <label className="pos-report-field">
                    <span>{tr("To")}</span>
                    <input
                      type="date"
                      value={reportTo}
                      min={reportFrom || undefined}
                      onChange={(event) => setReportTo(event.target.value)}
                    />
                  </label>
                  <label className="pos-report-field">
                    <span>{tr("Type")}</span>
                    <select
                      value={reportType}
                      onChange={(event) => setReportType(event.target.value)}
                    >
                      <option value="cash">{tr("Cash")}</option>
                      <option value="card">{tr("Card")}</option>
                      <option value="all">{tr("All")}</option>
                    </select>
                  </label>
                  <label className="pos-report-field">
                    <span>{tr("Limits")}</span>
                    <select
                      value={reportLimit}
                      onChange={(event) => setReportLimit(event.target.value)}
                    >
                      <option value="all">{tr("All")}</option>
                      {[10, 25, 50, 100, 200, 500].map((limitOption) => (
                        <option key={limitOption} value={limitOption}>
                          {limitOption}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <button
                  type="button"
                  className="pos-report-menu-btn"
                  disabled={!!reportLoading}
                  onClick={() => handleReportDownload("pdf")}
                >
                  <FileText className="pos-icon-sm" />
                  {reportLoading === "pdf"
                    ? tr("Downloading PDF...")
                    : tr("Download PDF")}
                </button>
                <button
                  type="button"
                  className="pos-report-menu-btn"
                  disabled={!!reportLoading}
                  onClick={() => handleReportDownload("excel")}
                >
                  <FileSpreadsheet className="pos-icon-sm" />
                  {reportLoading === "excel"
                    ? tr("Downloading Excel...")
                    : tr("Download Excel")}
                </button>
              </div>
            )}
          </div>
          <div className="pos-sidenav-bottom">
            <Link to="#" className="pos-sidenav-item" title={tr("Logout")}>
              <LogOut className="pos-icon-md" />
            </Link>
          </div>
        </nav>

        {/* ── Main area ── */}
        <div className="pos-main">
          {/* ── Top bar ── */}
          <header className="pos-topbar">
            <Link
              to="#"
              className="pos-topbar-btn teal"
              data-bs-toggle="modal"
              data-bs-target="#orders"
            >
              <ShoppingCart className="pos-icon-sm" /> {tr("View Orders")}
            </Link>
            <Link
              to="#"
              className="pos-topbar-btn indigo"
              data-bs-toggle="modal"
              data-bs-target="#reset"
            >
              <RotateCcw className="pos-icon-sm" /> {tr("Reset")}
            </Link>
            <Link
              to="#"
              className="pos-topbar-btn info"
              data-bs-toggle="modal"
              data-bs-target="#recents"
            >
              <RefreshCw className="pos-icon-sm" /> {tr("Transaction")}
            </Link>
            <div className="pos-cashier-badge">
              <div className="pos-cashier-avatar">
                <User className="pos-icon-sm" />
              </div>
              <div className="pos-cashier-info">
                <strong>{paymentReceiver || tr("Cashier")}</strong>
                <span>{tr("Cashier")}</span>
              </div>
            </div>
          </header>

          {/* ── Body ── */}
          <div className="pos-body">
            {/* ── Product panel ── */}
            <div className="pos-product-panel">
              <div className="pos-category-search">
                <span className="search-icon">
                  <Search className="pos-icon-sm" />
                </span>
                <input
                  type="text"
                  placeholder={tr("Search menu...")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <p className="pos-section-title pos-category-title">
                {tr("Choose Category")}
              </p>

              <div className="pos-cat-slider-wrap force-ltr">
                <button
                  type="button"
                  className="pos-cat-nav pos-cat-prev"
                  aria-label="Previous categories"
                  onClick={handleCategoryPrev}
                >
                  <ChevronLeft className="pos-icon-sm" />
                </button>
                <button
                  type="button"
                  className="pos-cat-nav pos-cat-next"
                  aria-label="Next categories"
                  onClick={handleCategoryNext}
                >
                  <ChevronRight className="pos-icon-sm" />
                </button>
                <Swiper
                  key="category-swiper-ltr"
                  className="pos-cat-slider"
                  modules={[Mousewheel, A11y]}
                  onSwiper={(swiper) => {
                    categorySwiperRef.current = swiper;
                  }}
                  rewind
                  watchOverflow
                  mousewheel={{ forceToAxis: true }}
                  dir="ltr"
                  slidesPerView={2.2}
                  spaceBetween={12}
                  breakpoints={{
                    480: { slidesPerView: 3.2 },
                    640: { slidesPerView: 4.1 },
                    768: { slidesPerView: 5.1 },
                    992: { slidesPerView: 6.2 },
                    1200: { slidesPerView: 7.2 },
                    1440: { slidesPerView: 8.2 },
                  }}
                >
                  {categories.map((cat) => {
                    const isActive = activeTab === cat._id;
                    const categoryLabel = getLocalizedCategoryName(cat.name);
                    const emoji = getCategoryEmoji(categoryLabel);
                    const imgUrl = cat.isLocalAsset
                      ? null
                      : toPublicUrl(cat.imageUrl || "");
                    const hasImage = Boolean(imgUrl && imgUrl.length > 0);
                    return (
                      <SwiperSlide key={cat._id}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveTab(cat._id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              setActiveTab(cat._id);
                          }}
                          className={`pos-cat-card ${isActive ? "active" : ""}`}
                        >
                          {hasImage ? (
                            <div className="pos-cat-img-wrap">
                              <img
                                src={imgUrl}
                                alt={categoryLabel}
                                onError={(e) => {
                                  e.currentTarget.parentElement.style.display =
                                    "none";
                                  const emojiEl =
                                    e.currentTarget.parentElement.nextSibling;
                                  if (emojiEl) emojiEl.style.display = "block";
                                }}
                              />
                            </div>
                          ) : null}
                          <div
                            className="pos-cat-emoji"
                            style={{ display: hasImage ? "none" : "block" }}
                          >
                            {emoji}
                          </div>
                          <div className="pos-cat-name">{categoryLabel}</div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>

              <div className="pos-products-header" style={{ marginTop: 28 }}>
                <p className="pos-section-title" style={{ marginBottom: 0 }}>
                  {(() => {
                    const activeCategory = categories.find(
                      (c) => c._id === activeTab,
                    );
                    return activeCategory
                      ? getLocalizedCategoryName(activeCategory.name)
                      : tr("All");
                  })()}{" "}
                  {tr("Menu")}
                </p>
                <button className="pos-sort-btn">
                  {tr("Sort by A-Z")} <ArrowUpDown className="pos-icon-sm" />
                </button>
              </div>

              <div className="pos-product-grid">
                {filteredProducts.length > 0 ? (
                  paginatedProducts.map((product) => {
                    const isSelected = selectedItems.some(
                      (item) => String(item._id) === String(product._id),
                    );
                    return (
                      <div
                        key={`${product._id}-${product.Product_Name || product.name}`}
                        className={`pos-prod-card ${isSelected ? "selected" : ""}`}
                        onClick={() => handleProductClick(product)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleProductClick(product);
                        }}
                      >
                        <div className="pos-prod-img-wrap">
                          <img
                            src={
                              toPublicUrl(product.image) ||
                              "/assets/img/products/shoes.jpg"
                            }
                            alt={product.Product_Name || product.name}
                            onError={(e) => {
                              e.currentTarget.src =
                                "/assets/img/products/shoes.jpg";
                            }}
                          />
                        </div>
                        <div className="pos-prod-body">
                          <div className="pos-prod-cat">
                            {product.Category || tr("Uncategorized")}
                          </div>
                          <div className="pos-prod-name">
                            {product.Product_Name ||
                              product.name ||
                              tr("Unnamed")}
                          </div>
                          <div className="pos-prod-footer">
                            <span className="pos-prod-qty">
                              {Number(product.Qty || 0)} {tr("pcs")}
                            </span>
                            <span className="pos-prod-price">
                              ${Number(product.Price || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        {Array.isArray(product.allVariants) &&
                          product.allVariants.length > 0 && (
                            <div className="pos-prod-variants">
                              {product.allVariants.map((variant) => (
                                <button
                                  key={variant._id}
                                  type="button"
                                  className="pos-prod-variant-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductClick(variant);
                                  }}
                                >
                                  {variant.name}
                                </button>
                              ))}
                            </div>
                          )}
                      </div>
                    );
                  })
                ) : (
                  <div className="pos-no-products">
                    <div className="icon">
                      <SearchX />
                    </div>
                    <p>{tr("No products found.")}</p>
                  </div>
                )}
              </div>

              {filteredProducts.length > PRODUCTS_PER_PAGE && (
                <div className="pos-products-pagination">
                  <button
                    type="button"
                    className="pos-page-btn"
                    disabled={productPage <= 1}
                    onClick={() =>
                      setProductPage((prev) => Math.max(1, prev - 1))
                    }
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="pos-icon-sm" />
                  </button>
                  {visiblePageButtons.map((page) => (
                    <button
                      key={page}
                      type="button"
                      className={`pos-page-btn ${productPage === page ? "active" : ""}`}
                      onClick={() => setProductPage(page)}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="pos-page-btn"
                    disabled={productPage >= totalProductPages}
                    onClick={() =>
                      setProductPage((prev) =>
                        Math.min(totalProductPages, prev + 1),
                      )
                    }
                    aria-label="Next page"
                  >
                    <ChevronRight className="pos-icon-sm" />
                  </button>
                  <span className="pos-page-meta">
                    {tr("Page")} {productPage} {tr("of")} {totalProductPages}
                  </span>
                </div>
              )}
            </div>

            {/* ════════════════════════════════════
                ── Billing panel ──
            ════════════════════════════════════ */}
            <div className="pos-billing-panel">
              <aside className="product-order-list">
                {/* ── 1. Customer info card ── */}
                <div className="pos-panel-card pos-customer-card">
                  <h4>{tr("Customer Information")}</h4>
                  <div className="input-block">
                    <Select
                      options={customerOptions}
                      classNamePrefix="react-select select"
                      placeholder={tr("Choose a Name")}
                      defaultValue={customerOptions[0]}
                    />
                  </div>
                  <div className="input-block">
                    <Select
                      options={productSearchOptions}
                      classNamePrefix="react-select select"
                      placeholder={tr("Search Products")}
                      isClearable
                      onChange={(option) => {
                        const product = filteredProducts.find(
                          (e) => String(e._id) === String(option?.value),
                        );
                        if (product) handleProductClick(product);
                      }}
                    />
                  </div>
                </div>

                {/* ── 2. Cart card ── */}
                <div className="pos-cart-card">
                  {/* Header */}
                  <div className="head-text">
                    <div className="cart-head-left">
                      <h5>
                        {tr("Product Added")}
                        <span className="count">{selectedItems.length}</span>
                      </h5>
                      <span className="cart-head-total">
                        {tr("Cart Total")}: ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    <Link
                      to="#"
                      className="link-danger"
                      onClick={(e) => {
                        e.preventDefault();
                        handleClearAll();
                      }}
                    >
                      <X className="pos-icon-sm" />
                      {tr("Clear all")}
                    </Link>
                  </div>

                  {/* Items list */}
                  <div className="pos-cart-items">
                    {selectedItems.length === 0 ? (
                      <div className="empty-cart">
                        <div style={{ marginBottom: 8 }}>
                          <ShoppingCart
                            className="pos-icon-lg"
                            style={{ opacity: 0.4 }}
                          />
                        </div>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>
                          {tr("No Products Selected")}
                        </p>
                      </div>
                    ) : (
                      selectedItems.map((item) => {
                        const displayName = getItemDisplayName(item);
                        const variantName = getItemVariantName(item);
                        const categoryName = getItemCategoryName(item);
                        const itemSku =
                          item.SKU ||
                          item.sku ||
                          item.productSku ||
                          item.productSKU ||
                          item.Product_Code ||
                          item._id;
                        const lineTotal =
                          Number(item.Price || 0) * Number(item.quantity || 0);
                        return (
                          <div className="pos-cart-item" key={item._id}>
                            {/* Image */}
                            <Link
                              to="#"
                              className="pos-cart-item__img"
                              onClick={(e) => e.preventDefault()}
                            >
                              <img
                                src={
                                  toPublicUrl(item.image) ||
                                  "/assets/img/products/shoes.jpg"
                                }
                                alt={displayName}
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "/assets/img/products/shoes.jpg";
                                }}
                              />
                            </Link>

                            {/* Info */}
                            <div className="pos-cart-item__info">
                              <div className="pos-cart-item__meta">
                                <span className="pos-cart-item__sku">
                                  {String(itemSku || "")
                                    .slice(-10)
                                    .toUpperCase()}
                                </span>
                                <span className="pos-cart-item__tag pos-cart-item__tag--category">
                                  {categoryName}
                                </span>
                                {variantName && (
                                  <span className="pos-cart-item__tag pos-cart-item__tag--variant">
                                    {variantName}
                                  </span>
                                )}
                              </div>
                              <h6 className="pos-cart-item__name">
                                <Link
                                  to="#"
                                  onClick={(e) => e.preventDefault()}
                                >
                                  {displayName}
                                </Link>
                              </h6>
                              <div className="pos-cart-item__pricing">
                                <span className="pos-cart-item__chip">
                                  ${Number(item.Price || 0).toFixed(2)}
                                </span>
                                <span className="pos-cart-item__chip">
                                  x{item.quantity}
                                </span>
                                <strong className="pos-cart-item__total">
                                  ${lineTotal.toFixed(2)}
                                </strong>
                              </div>
                            </div>

                            {/* Actions: delete + qty */}
                            <div className="pos-cart-item__actions">
                              <Link
                                className="pos-cart-item__remove"
                                to="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveItem(item._id);
                                }}
                                title={tr("Remove")}
                              >
                                <Trash2 style={{ width: 12, height: 12 }} />
                              </Link>
                              <div className="pos-cart-item__qty">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item._id,
                                      item.quantity - 1,
                                    )
                                  }
                                >
                                  -
                                </button>
                                <span className="pos-cart-item__qty-count">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item._id,
                                      item.quantity + 1,
                                    )
                                  }
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ── 3. Summary card ── */}
                <div className="pos-panel-card pos-summary-card">
                  <div className="selling-info">
                    <div className="row g-3">
                      {/* <div className="col-12 col-sm-4">
                        <label className="form-label">{tr("Order Tax")}</label>
                        <Select
                          options={taxOptions}
                          classNamePrefix="react-select select"
                          value={taxOptions.find(
                            (o) => o.value === orderTaxRate,
                          )}
                          onChange={(o) =>
                            setOrderTaxRate(Number(o?.value || 0))
                          }
                        />
                      </div> */}
                      <div className="col-12 col-sm-4">
                        <label className="form-label">{tr("Shipping")}</label>
                        <Select
                          options={shippingOptions}
                          classNamePrefix="react-select select"
                          value={shippingOptions.find(
                            (o) => o.value === shippingFee,
                          )}
                          onChange={(o) =>
                            setShippingFee(Number(o?.value || 0))
                          }
                        />
                      </div>
                      <div className="col-12 col-sm-4">
                        <label className="form-label">{tr("Discount")}</label>
                        <Select
                          options={discountOptions}
                          classNamePrefix="react-select select"
                          value={discountOptions.find(
                            (o) => o.value === discountRate,
                          )}
                          onChange={(o) =>
                            setDiscountRate(Number(o?.value || 0))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="order-total">
                    <table className="table table-responsive table-borderless">
                      <tbody>
                        <tr>
                          <td>{tr("Sub Total")}</td>
                          <td className="text-end">${subtotal.toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td>{tr("Product Tax")}</td>
                          <td className="text-end">${itemTax.toFixed(2)}</td>
                        </tr>
                        {/* <tr>
                          <td>
                            {tr("Tax")} (
                            {selectedTaxOption?.label || `${orderTaxRate}%`})
                          </td>
                          <td className="text-end">${orderTax.toFixed(2)}</td>
                        </tr> */}
                        <tr>
                          <td>{tr("Shipping")}</td>
                          <td className="text-end">
                            ${shippingFee.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-danger">
                            {tr("Discount")} ({discountRate}%)
                          </td>
                          <td className="text-danger text-end">
                            -${discount.toFixed(2)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>{tr("Total")}</strong>
                          </td>
                          <td className="text-end">
                            <strong>${totalPayable.toFixed(2)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── 4. Payment method ── */}
                <div
                  className="block-section payment-method"
                  style={{ padding: "0 12px" }}
                >
                  <h4
                    style={{
                      padding: "10px 0 6px",
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {tr("Payment Method")}
                  </h4>
                  <div className="row align-items-center justify-content-center methods g-3">
                    <div className="col-sm-6 col-md-4">
                      <Link
                        to="#"
                        className={`payment-item ${paymentType.value === "cash" ? "active" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setPaymentType({ value: "cash", label: tr("Cash") });
                        }}
                      >
                        <Banknote className="pos-icon" />
                        <span>{tr("Cash")}</span>
                      </Link>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <Link
                        to="#"
                        className={`payment-item ${paymentType.value === "card" ? "active" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setPaymentType({
                            value: "card",
                            label: tr("Debit Card"),
                          });
                        }}
                      >
                        <CreditCard className="pos-icon" />
                        <span>{tr("Debit Card")}</span>
                      </Link>
                    </div>
                    <div className="col-sm-6 col-md-4">
                      <Link
                        to="#"
                        className={`payment-item ${paymentType.value === "scan" ? "active" : ""}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setPaymentType({ value: "scan", label: tr("Scan") });
                        }}
                      >
                        <ScanLine className="pos-icon" />
                        <span>{tr("Scan")}</span>
                      </Link>
                    </div>
                  </div>
                </div>

                {/* ── 5. Payment details ── */}
                <div className="block-section" style={{ padding: "0 12px" }}>
                  <h4
                    className="mb-3"
                    style={{
                      padding: "10px 0 0",
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  >
                    {tr("Payment Details")}
                  </h4>
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label">
                        {tr("Received Amount")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={receivedAmount}
                        onChange={(e) => setReceivedAmount(e.target.value)}
                        disabled={paymentType?.value !== "cash"}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">
                        {tr("Paying Amount")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={payingAmount}
                        onChange={(e) => setPayingAmount(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label">{tr("Change Amount")}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={change}
                        readOnly
                      />
                    </div>
                    {paymentType?.value === "cash" && (
                      <div className="col-12">
                        <label className="form-label">{tr("Quick Cash")}</label>
                        <div className="d-flex flex-wrap gap-2">
                          {quickCashAmounts.map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => handleQuickCashAdd(amount)}
                            >
                              + {amount}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="col-12">
                      <label className="form-label">
                        {tr("Payment Receiver")}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={paymentReceiver}
                        onChange={(e) => setPaymentReceiver(e.target.value)}
                        placeholder={tr("Enter payment receiver")}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">{tr("Payment Note")}</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        placeholder={tr("Enter payment note")}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">{tr("Sale Note")}</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={saleNote}
                        onChange={(e) => setSaleNote(e.target.value)}
                        placeholder={tr("Enter sale note")}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">{tr("Staff Note")}</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={staffNote}
                        onChange={(e) => setStaffNote(e.target.value)}
                        placeholder={tr("Enter staff note")}
                      />
                    </div>
                  </div>
                </div>

                {/* ── 6. Checkout button ── */}
                <div className="btn-block" style={{ padding: "10px 12px 0" }}>
                  <button
                    type="button"
                    className="btn btn-secondary w-100"
                    disabled={isCheckingOut}
                    onClick={handleGrandTotalCheckout}
                  >
                    {isCheckingOut ? (
                      <span className="d-inline-flex align-items-center gap-2">
                        <span className="checkout-spinner" />
                        {tr("Processing...")}
                      </span>
                    ) : (
                      `${tr("Add to Billing")} : $${totalPayable.toFixed(2)}`
                    )}
                  </button>
                </div>

                {/* ── 7. Print row ── */}
                <div
                  className="btn-row d-flex align-items-center justify-content-between gap-2"
                  style={{ padding: "8px 12px 16px" }}
                >
                  <div className="d-flex align-items-center flex-fill gap-2">
                    <select
                      className="form-select"
                      style={{
                        maxWidth: 170,
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                      value={invoiceLang}
                      disabled
                    >
                      <option value="en">{tr("English Invoice")}</option>
                      <option value="ar">{tr("Arabic Invoice")}</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-white d-flex align-items-center justify-content-center flex-fill m-0"
                      onClick={() => printInvoice(invoiceLang)}
                    >
                      <Printer className="pos-icon me-2 ms-2" />
                      {tr("Print Order")}
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stock modal ── */}
      {stockModal.show && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <TriangleAlert className="pos-icon me-2" />
                  {tr("Stock Warning")}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() =>
                    setStockModal({ show: false, productName: "", maxQty: 0 })
                  }
                />
              </div>
              <div className="modal-body text-center py-4">
                {stockModal.maxQty === 0 ? (
                  <>
                    <h5 className="text-danger mb-2">
                      {stockModal.productName}
                    </h5>
                    <p className="mb-0">
                      {tr("This item is currently out of stock.")}
                    </p>
                  </>
                ) : (
                  <>
                    <h5 className="mb-2">{stockModal.productName}</h5>
                    <p className="mb-0">
                      {tr("Only")} <strong>{stockModal.maxQty}</strong>{" "}
                      {tr("item(s) available in stock.")}
                    </p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setStockModal({ show: false, productName: "", maxQty: 0 })
                  }
                >
                  {tr("OK")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Status popup ── */}
      {statusPopup.show && (
        <div className="status-popup-overlay">
          <div className="status-popup-card">
            <div className="status-popup-top">
              <div
                className={`status-popup-icon ${statusPopup.type === "success" ? "success" : "error"}`}
              >
                {statusPopup.type === "success" ? (
                  <Check className="pos-icon-lg" />
                ) : (
                  <X className="pos-icon-lg" />
                )}
              </div>
              <h3 className="status-popup-title">{statusPopup.title}</h3>
              <p className="status-popup-message">{statusPopup.message}</p>
            </div>
            <div className="status-popup-footer">
              <button
                type="button"
                className="status-popup-btn"
                onClick={closeStatusPopup}
              >
                {tr("OK")}
              </button>
            </div>
          </div>
        </div>
      )}

      <PosModals
        selectedItems={selectedItems}
        totalAmount={totalPayable}
        receivedAmount={receivedAmount}
        setReceivedAmount={setReceivedAmount}
        payingAmount={payingAmount}
        setPayingAmount={setPayingAmount}
        change={change}
        setChange={setChange}
        paymentType={paymentType}
        setPaymentType={setPaymentType}
        paymentReceiver={paymentReceiver}
        setPaymentReceiver={setPaymentReceiver}
        paymentNote={paymentNote}
        setPaymentNote={setPaymentNote}
        saleNote={saleNote}
        setSaleNote={setSaleNote}
        staffNote={staffNote}
        setStaffNote={setStaffNote}
        handlePlaceOrder={handlePlaceOrder}
        invoiceLang={invoiceLang}
        onPrintRecentTransaction={printInvoice}
      />
    </>
  );
};

export default Pos2;

